import {
    DASHBOARD_SKIP_CACHE,
    DASHBOARD_STALE_TIME_MS,
} from "@/core/constants/dashboard.constants";
import {
    buildDashboardQueryKey,
    getVersionTokenForWidget,
} from "@/core/hooks/dashboardQueryKeys";
import { useDashboardStore } from "@/core/hooks/useDashboardStore";
import type {
    DashboardEnvelope,
    DashboardScope,
    DashboardWidgetName,
} from "@/core/interfaces/Dashboard";
import { useQuery } from "@tanstack/vue-query";
import { computed, type ComputedRef, type Ref } from "vue";

type MaybeRefScope =
  | Ref<DashboardScope | null>
  | ComputedRef<DashboardScope | null>;

/**
 * Shared dashboard widget query. Components must use this instead of direct axios.
 */
export function useDashboardQuery<T>(
  widget: DashboardWidgetName,
  scope: MaybeRefScope,
  fetcher: (scope: DashboardScope) => Promise<DashboardEnvelope<T>>,
  options: {
    filters?:
      | Ref<Record<string, unknown>>
      | ComputedRef<Record<string, unknown>>;
    enabled?: Ref<boolean> | ComputedRef<boolean> | boolean;
  } = {}
) {
  const { versions, metaLoaded } = useDashboardStore();

  const cacheVersion = computed(() => {
    if (DASHBOARD_SKIP_CACHE) {
      return null;
    }
    return getVersionTokenForWidget(widget, versions.value, scope.value);
  });

  const filters = computed(() => options.filters?.value ?? {});

  const queryKey = computed(() =>
    buildDashboardQueryKey(
      widget,
      scope.value,
      filters.value,
      cacheVersion.value
    )
  );

  const enabled = computed(() => {
    const extra =
      typeof options.enabled === "boolean"
        ? options.enabled
        : options.enabled?.value ?? true;
    return Boolean(metaLoaded.value && scope.value && extra);
  });

  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const currentScope = scope.value;
      if (!currentScope) {
        throw new Error("Dashboard scope is not ready");
      }
      return fetcher(currentScope);
    },
    enabled,
    staleTime: DASHBOARD_SKIP_CACHE ? 0 : DASHBOARD_STALE_TIME_MS[widget],
    gcTime: DASHBOARD_SKIP_CACHE ? 0 : undefined,
    // Keep prior widget payload visible while the next scope/filter fetch runs.
    placeholderData: (previousData) => previousData,
    refetchOnWindowFocus: false,
    shallow: true,
    throwOnError: false,
  });

  const data = computed(() => query.data.value?.data ?? null);
  const meta = computed(() => query.data.value?.meta ?? null);
  const isStale = computed(
    () =>
      Boolean(meta.value?.cache?.isStale) ||
      (query.isError.value && Boolean(query.data.value))
  );
  const isEmpty = computed(() => {
    if (!data.value) {
      return !query.isLoading.value && !query.isFetching.value;
    }
    if (Array.isArray(data.value)) {
      return data.value.length === 0;
    }
    if (
      typeof data.value === "object" &&
      data.value !== null &&
      "items" in data.value &&
      Array.isArray((data.value as { items: unknown[] }).items)
    ) {
      return (data.value as { items: unknown[] }).items.length === 0;
    }
    return false;
  });

  return {
    query,
    data,
    meta,
    isLoading: computed(() => query.isLoading.value && !query.data.value),
    isFetching: computed(() => query.isFetching.value),
    isError: computed(() => query.isError.value && !query.data.value),
    error: computed(() => query.error.value),
    isStale,
    isEmpty,
    generatedAt: computed(() => meta.value?.cache?.generatedAt ?? null),
    refetch: query.refetch,
  };
}
